import { useMemo } from "react";

/**
 * Helper to create auto-flex sizing for data grid columns
 * @param totalFlex - Total flex to distribute (default: 1.0)
 * @param importantColumns - Object mapping column names to their fixed flex values
 * @param allColumns - Array of all column names
 * @returns Map with flex values for each column
 */
const createAutoFlex = (
  totalFlex: number = 1.0,
  importantColumns: Record<string, number>,
  allColumns: string[]
): Map<string, number> => {
  const flexMap = new Map<string, number>();

  // Calculate remaining flex after important columns
  const usedFlex = Object.values(importantColumns).reduce((sum, flex) => sum + flex, 0);
  const remainingFlex = totalFlex - usedFlex;

  // Get columns that need auto-sizing
  const autoColumns = allColumns.filter((col) => !(col in importantColumns));
  const flexPerAutoColumn = autoColumns.length > 0 ? remainingFlex / autoColumns.length : 0;

  // Set flex for all columns
  allColumns.forEach((column) => {
    if (column in importantColumns) {
      flexMap.set(column, importantColumns[column]);
    } else {
      flexMap.set(column, flexPerAutoColumn);
    }
  });

  return flexMap;
};

/**
 * Hook for auto-distributing flex values across data grid columns
 * @param totalFlex - Total flex to distribute across all columns
 * @param importantColumns - Columns that need specific flex values
 * @param allColumns - All column field names
 * @returns Map with flex values for each column
 */
export const useAutoFlex = (
  totalFlex: number = 1.0,
  importantColumns: Record<string, number>,
  allColumns: string[]
) => {
  return useMemo(
    () => createAutoFlex(totalFlex, importantColumns, allColumns),
    [totalFlex, importantColumns, allColumns]
  );
};
